/**
 * Sign-in — SPEC §10, §11.2, §11.6.
 *
 * Two steps: enter an address, then either tap the link in the email or type the code.
 *
 * Three things here follow the spec closely enough to be worth naming:
 *
 *  - The code field is **secondary**. The email's button is the fast path, so this screen
 *    leads with "check your email" and offers the code underneath, rather than presenting
 *    them as equal options (SPEC §11.2).
 *  - An unrecognized address says so, within the server's per-IP allowance (SPEC §10). The
 *    server decides; this screen just renders what it is told.
 *  - `admin@gearupwithcpr.com` is the only contact shown. No phone, and not Luke's direct
 *    address — a login page is where a social engineer starts (SPEC §11.6).
 */

import { useState } from 'react';
import { ApiError, api } from '../lib/api.js';
import { useSession } from '../lib/session.js';

const SUPPORT_EMAIL = 'admin@gearupwithcpr.com';

type Step = 'email' | 'code';

export function SignIn() {
  const { refresh } = useSession();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'info' | 'error' | 'warn'; text: string } | null>(null);

  async function requestLink(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      const result = await api.signIn.request(email);
      if (result.unrecognized) {
        setNotice({ tone: 'warn', text: result.message });
      } else {
        setNotice({ tone: 'info', text: result.message });
        setStep('code');
      }
    } catch (err) {
      setNotice({
        tone: 'error',
        text: err instanceof ApiError ? err.message : 'Something went wrong. Please try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await api.signIn.verifyCode(email, code, remember);
      await refresh();
    } catch (err) {
      setNotice({
        tone: 'error',
        text: err instanceof ApiError ? err.message : 'Something went wrong. Please try again.',
      });
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card stack">
      <h1>{step === 'email' ? 'Sign in' : 'Check your email'}</h1>

      {step === 'email' ? (
        <>
          <p className="muted">
            Enter the email address we have for you and we'll send you a link. There's no
            password to remember.
          </p>

          <form onSubmit={requestLink} className="stack">
            <label className="field">
              <span className="field__label">Email address</span>
              <input
                className="input"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>

            {notice && <Notice tone={notice.tone} text={notice.text} />}

            <button className="btn btn--primary btn--block" type="submit" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Email me a link'}
            </button>
          </form>
        </>
      ) : (
        <>
          {notice && <Notice tone={notice.tone} text={notice.text} />}

          <p>
            We've sent a link to <strong>{email}</strong>. Tap the button in that email and
            you're in.
          </p>

          <details>
            <summary style={{ cursor: 'pointer', minHeight: 40, display: 'flex', alignItems: 'center' }}>
              Or enter the 6-digit code instead
            </summary>

            <form onSubmit={submitCode} className="stack" style={{ marginTop: 12 }}>
              <label className="field">
                <span className="visually-hidden">6-digit code</span>
                <input
                  className="input input--code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="000000"
                  aria-label="6-digit code"
                />
              </label>

              <label className="row">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  style={{ width: 20, height: 20, minHeight: 0 }}
                />
                <span className="small">Remember this device for 30 days</span>
              </label>

              <button className="btn btn--primary btn--block" type="submit" disabled={busy || code.replace(/\s/g, '').length < 6}>
                {busy ? 'Checking…' : 'Sign in'}
              </button>
            </form>
          </details>

          <button
            className="btn btn--ghost btn--block"
            type="button"
            onClick={() => {
              setStep('email');
              setCode('');
              setNotice(null);
            }}
          >
            Use a different email address
          </button>
        </>
      )}

      <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '4px 0' }} />

      <p className="small muted">
        Trouble signing in? Email{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--navy)' }}>
          {SUPPORT_EMAIL}
        </a>{' '}
        and we'll sort it out.
      </p>
    </div>
  );
}

function Notice({ tone, text }: { tone: 'info' | 'error' | 'warn' | 'success'; text: string }) {
  const icon = { info: 'ℹ', warn: '!', error: '!', success: '✓' }[tone];
  return (
    <div className={`notice notice--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="notice__icon" aria-hidden="true">
        {icon}
      </span>
      <span>{text}</span>
    </div>
  );
}
